// Die Einführungstour — Inhalt und Ablauf an einem Ort.
//
// Diese Datei beschreibt NUR, was passieren soll. Wie es passiert, steht
// in tourLauf.ts. Der Grund für die Trennung ist Wartbarkeit: wer einen
// Text ändert oder einen Schritt verschiebt, soll das hier tun können,
// ohne die Mechanik zu verstehen.
//
// Anker sind ausschließlich `data-tour="…"`-Attribute. Niemals
// CSS-Klassen oder DOM-Pfade: Klassen ändern sich beim nächsten
// Layout-Feinschliff, ein Anker ist eine Zusage. Dass jeder hier
// genannte Anker wirklich existiert, prüft der Test in
// src/test/tourAnker.test.tsx bei jedem Build.

/** Wo ein Schritt spielt. Der Anker-Wächter rendert danach. */
export type TourKontext =
  | 'global' // Sidebar und Kopfzeile — auf jeder Seite da
  | 'dashboard'
  | 'sheet' // Formular "Neue Bestellung", geöffnet
  | 'detail'
  | 'sendungsdialog'
  | 'hilfe'; // Hilfe-Panel, geöffnet

/**
 * Was vor einem Schritt geschehen muss. Bewusst eine kurze, feste Liste
 * statt einer Mini-Sprache: alles, was die Tour an der Anwendung tut,
 * ist damit auf einen Blick aufzählbar.
 */
export type TourAktion =
  | 'zum-dashboard'
  | 'sheet-oeffnen'
  | 'sheet-schliessen'
  // Auf schmalen Geräten liegen Nutzerwechsel und Hilfe im Menü der
  // Bottom-Navigation. Am Schreibtisch stehen beide ohnehin im Bild —
  // dort tut die Aktion nichts.
  | 'menue-oeffnen'
  | 'uebung-sicherstellen' // vorhandene Übung öffnen, sonst eine anlegen
  | 'uebung-neu' // immer eine frische anlegen
  | 'aufraeumen';

/**
 * Woran die Tour erkennt, dass der Mitarbeiter den Schritt gemacht hat.
 * Ist die Erwartung erfüllt, geht es von selbst weiter — wer lieber
 * zusieht, kommt mit "Weiter" trotzdem durch.
 */
export type TourErwartung =
  | { art: 'anker-da'; anker: string }
  | { art: 'anker-weg'; anker: string }
  | { art: 'route-enthaelt'; teil: string }
  // Vorsicht bei 'text-in-anker': der Text muss VORHER fehlen. Eine
  // Beschriftung, die ohnehin dasteht, ist sofort erfüllt und der Schritt
  // huscht vorbei, ehe ihn jemand gelesen hat. Genau so passiert beim
  // Durchspielen — deshalb gibt es für Haken jetzt 'gedrueckt'.
  | { art: 'text-in-anker'; anker: string; enthaelt: string }
  /** Ein Schalter mit aria-pressed innerhalb des Ankers ist gesetzt. */
  | { art: 'gedrueckt'; anker: string; beschriftung: string };

export type TourSchritt = {
  /** data-tour-Name des Elements, das hervorgehoben wird. */
  anker?: string;
  /**
   * Weitere Anker, von denen derselbe Schritt spricht.
   *
   * Hervorgehoben wird weiterhin nur `anker` — der Scan-Knopf sitzt IM
   * Suchfeld und ist damit ohnehin im Bild. Der Wächter-Test liest diese
   * Liste aber mit: sagt ein Schritt "die Kamera rechts", muss es die
   * Kamera auch geben. Ohne das Feld stünde der neue Anker in keinem
   * Verzeichnis und niemand merkte, wenn er wegfällt.
   */
  mitAnker?: string[];
  kontext: TourKontext;
  titel: string;
  text: string;
  vorher?: TourAktion;
  erwartung?: TourErwartung;
  /** Seite, auf der die Sprechblase steht. Ohne Angabe entscheidet driver.js. */
  seite?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * Überschrift des Kapitels, zu dem der Schritt gehört. Wird beim
   * Zusammenstellen gesetzt, nicht von Hand gepflegt — sie steht im
   * Fortschritt der Sprechblase.
   */
  kapitelTitel?: string;
};

export type KapitelId = 'orientierung' | 'erfassung' | 'lebenszyklus' | 'wissen';

export type TourKapitel = {
  id: KapitelId;
  titel: string;
  /** Ein Satz fürs Hilfe-Panel. */
  kurz: string;
  /** Legt das Kapitel beim Einzelstart eine Übungsbestellung an? */
  brauchtUebung: boolean;
  schritte: TourSchritt[];
};

/**
 * Eine ISBN als Chip zum Antippen: zeigt die Striche, kopiert die nackten
 * Ziffern. Den Klick fängt tourLauf.ts ab.
 *
 * Die Striche stehen hier fest im Text und werden nicht zur Laufzeit
 * gerechnet — tour.ts soll Inhalt bleiben und nichts importieren, was
 * Logik mitbringt. Dass beide Formen zusammenpassen, prüft der Test in
 * src/test/tourIsbn.test.ts.
 */
function isbnChip(nackt: string): string {
  const mitStrichen = ISBN_ANZEIGE[nackt];
  return (
    `<button type="button" class="engel-tour-isbn" data-isbn-kopieren="${nackt}" ` +
    `title="Antippen kopiert ${nackt}">${mitStrichen}</button>`
  );
}

/** Übungs-ISBN in beiden Formen. Bewusst kurz und handverlesen. */
const ISBN_ANZEIGE: Record<string, string> = {
  '9783423282789': '978-3-423-28278-9'
};

// ---------------------------------------------------------------------
// Kapitel 1 — Orientierung
// ---------------------------------------------------------------------

const ORIENTIERUNG: TourSchritt[] = [
  {
    kontext: 'dashboard',
    vorher: 'zum-dashboard',
    titel: 'Willkommen',
    text:
      'Zuerst schauen wir nur — angelegt wird nichts. Später übst du an einer ' +
      'Bestellung, die nur du siehst.'
  },
  {
    anker: 'dashboard-tabelle',
    kontext: 'dashboard',
    titel: 'Das Dashboard',
    // Bewusst ohne „Zeile": am Telefon sind es Karten, am Schreibtisch
    // Tabellenzeilen. Der Satz muss in beiden Layouts stimmen.
    text: 'Hier stehen alle offenen Bestellungen. Ein Tippen öffnet eine davon.',
    seite: 'top'
  },
  {
    anker: 'dashboard-quelle',
    kontext: 'dashboard',
    titel: 'Woher die Bestellung kommt',
    text:
      '„Laden" nehmen wir selbst auf — Telefon, Mail, Tresen. „Glomer" kommt ohne ' +
      'unser Zutun aus dem Online-Shop.',
    seite: 'bottom'
  },
  {
    anker: 'dashboard-suche',
    mitAnker: ['scan-button'],
    kontext: 'dashboard',
    titel: 'Suchen',
    text:
      'Am schnellsten findest du eine Bestellung über ihre Nummer. Sie steht auf ' +
      'dem Zettel am Buch und auf dem Lieferschein. Mit der Kamera rechts im Feld ' +
      'scannst du den QR vom Zettel oder den Barcode vom Buchrücken direkt hier.',
    seite: 'bottom'
  },
  {
    anker: 'wareneingang-scan',
    kontext: 'dashboard',
    titel: 'Ware eintragen',
    text:
      'Am Auspacktisch geht es andersherum: du hast das Buch und suchst die ' +
      'Bestellung. Hier scannst du den Barcode vom Buchrücken, siehst sofort, wer ' +
      'darauf wartet, und trägst es mit einem Tipp ein. Danach ist der Scanner ' +
      'gleich wieder scharf — Buch für Buch, ohne die Kamera neu zu starten.',
    seite: 'bottom'
  },
  {
    anker: 'dashboard-zustand',
    kontext: 'dashboard',
    titel: 'Wie weit ist sie?',
    text:
      'Das farbige Feld zeigt den Zustand — von neu bis abgeschlossen. Daran ' +
      'siehst du, was als Nächstes dran ist.',
    seite: 'left'
  },
  {
    anker: 'dashboard-abholung',
    kontext: 'dashboard',
    titel: 'Zugesagte Termine',
    text:
      'Hier steht der zugesagte Termin, zum Abholen wie zum Liefern. Ist der Tag ' +
      'vorbei, heißt es „überfällig seit …".',
    seite: 'left'
  },
  {
    anker: 'dashboard-filter',
    kontext: 'dashboard',
    titel: 'Filtern',
    text:
      'Blendet aus, was gerade nicht interessiert — etwa alles außer deinen ' +
      'eigenen. Die Zeile ganz unten zählt mit.',
    seite: 'bottom'
  }
];

// ---------------------------------------------------------------------
// Kapitel 2 — Erfassung üben
// ---------------------------------------------------------------------

const ERFASSUNG: TourSchritt[] = [
  {
    anker: 'neue-bestellung',
    kontext: 'global',
    vorher: 'zum-dashboard',
    titel: 'Jetzt selbst',
    // Der Knopf heißt am Schreibtisch „Neue Bestellung" und in der
    // Bottom-Navigation aus Platzgründen „Neu". Das Pluszeichen tragen
    // beide — daran ist er in jedem Layout zu erkennen.
    text:
      'Nimm eine Bestellung auf, als stünde die Kundin vor dir. Tipp auf den ' +
      'Knopf mit dem Pluszeichen.',
    erwartung: { art: 'anker-da', anker: 'sheet' },
    seite: 'right'
  },
  {
    anker: 'sheet-bestellweg',
    kontext: 'sheet',
    vorher: 'sheet-oeffnen',
    titel: 'Wie kam die Bestellung herein?',
    text:
      'Wähl „Telefon". Der Bestellweg hilft später beim Nachfragen: ' +
      'wer telefonisch bestellt hat, will meist auch angerufen werden.',
    seite: 'left'
  },
  {
    anker: 'sheet-kunde',
    kontext: 'sheet',
    titel: 'Kundin suchen',
    text:
      'Tipp „Erika" ein — ab zwei Zeichen wird gesucht. Enter übernimmt den ' +
      'obersten Treffer.',
    seite: 'left'
  },
  {
    anker: 'sheet-isbn',
    kontext: 'sheet',
    titel: 'Titel über die ISBN',
    // Der Chip ist echtes HTML. driver.js rendert die Beschreibung per
    // innerHTML — unbedenklich, weil hier ausschliesslich fest
    // geschriebene Tour-Texte stehen und nie Kunden- oder Titeldaten.
    // Angezeigt wird die ISBN mit Strichen wie ueberall sonst; kopiert
    // werden die nackten Ziffern (siehe tourLauf.ts).
    text:
      'Strichcode scannen oder ISBN tippen, dann Enter. Die Titeldaten kommen aus ' +
      'dem Verzeichnis lieferbarer Bücher.' +
      `<span class="engel-tour-isbn-zeile">Zum Üben: ${isbnChip('9783423282789')}</span>`,
    seite: 'left'
  },
  {
    anker: 'sheet-freitext',
    kontext: 'sheet',
    titel: 'Ohne ISBN',
    text:
      'Nicht alles hat eine ISBN. Schreib „Wandkalender 2027" hinein und klick ' +
      'auf „Zufügen".',
    // Echte Übung statt bloßer Erklärung. Nebenwirkung mit Absicht: ab
    // hier hat die Bestellung wirklich zwei Positionen, und Kapitel 3
    // kann von „beiden Positionen" sprechen, ohne zu lügen (B92, B19).
    erwartung: { art: 'text-in-anker', anker: 'sheet-positionen', enthaelt: 'Wandkalender 2027' },
    seite: 'left'
  },
  {
    anker: 'sheet-abholdatum',
    kontext: 'sheet',
    titel: 'Wann will sie kommen?',
    text:
      'Optional, aber hilfreich: Steht hier ein Termin, meldet sich das ' +
      'Dashboard von selbst, wenn er verstreicht.',
    seite: 'left'
  },
  {
    anker: 'sheet-speichern',
    kontext: 'sheet',
    titel: 'Speichern',
    text:
      'Fehlt noch etwas, sagt der Knopf es dir. Danach landest du direkt in ' +
      'der Bestellung.',
    erwartung: { art: 'route-enthaelt', teil: '/bestellungen/' },
    seite: 'top'
  }
];

// ---------------------------------------------------------------------
// Kapitel 3 — Lebenszyklus üben
// ---------------------------------------------------------------------

const LEBENSZYKLUS: TourSchritt[] = [
  {
    anker: 'detail-kopf',
    kontext: 'detail',
    vorher: 'uebung-sicherstellen',
    titel: 'Die Bestellung',
    text:
      'Das ist die Übungsbestellung: am Ü erkennbar, für andere unsichtbar. ' +
      'Von hier aus führst du sie bis zum Schluss.',
    seite: 'bottom'
  },
  {
    // Früher wurde hier direkt der Zustand gesetzt. Seit es das
    // Beschaffungs-Journal gibt, ist das die falsche Reihenfolge: erst
    // wird aufgeschrieben, WO bestellt wurde, der Zustand folgt daraus.
    // Wer nur den Zustand umstellt, hat nichts festgehalten — und die
    // Kollegin weiß morgen nicht, bei wem sie nachhaken soll.
    anker: 'detail-positionen',
    kontext: 'detail',
    titel: 'Bestellt — aber wo?',
    text:
      'Du hast bestellt: trag es ein. Bei jeder Position „+ Beschaffung", ' +
      'Weg wählen — etwa „Libri" —, die Menge steht schon, dann „Zufügen". ' +
      'Beide Positionen, bitte.',
    // Bis A7.3 wartete dieser Schritt auf den Knopf „Alles bestellt" und
    // ein Klick darauf. Den Knopf gibt es nicht mehr: der Zustand springt
    // von selbst, sobald alle Positionen einen Weg haben (E4). Gewartet
    // wird deshalb direkt auf das Ergebnis — dasselbe, worauf der
    // nachfolgende Schritt ohnehin schon wartete.
    erwartung: { art: 'text-in-anker', anker: 'detail-zustand', enthaelt: 'Bestellt' },
    seite: 'top'
  },
  {
    anker: 'detail-zustand',
    kontext: 'detail',
    titel: 'Von selbst auf „Bestellt"',
    text:
      'Sieh nach oben: der Zustand ist umgesprungen, ohne dass du ihn ' +
      'angefasst hast. Sobald bei jeder Position steht, wo bestellt wurde, ' +
      'ist das eine Tatsache — daran ist nichts zu entscheiden.',
    // Rechts daneben statt darunter: das Zustands-Menü klappt nach unten
    // auf und läge sonst unter der Sprechblase.
    seite: 'top'
  },
  {
    anker: 'detail-positionen',
    kontext: 'detail',
    titel: 'Ware eintreffen lassen',
    text:
      'Das Paket ist da. Zähl bei jeder Position den Eingang hoch, bis die ' +
      'bestellte Menge erreicht ist. Beide Positionen, bitte.',
    erwartung: { art: 'anker-da', anker: 'detail-bereit' },
    seite: 'top'
  },
  {
    anker: 'detail-checkliste',
    kontext: 'detail',
    titel: 'Von selbst umgesprungen',
    text:
      '„Eingetroffen" hat sich selbst gesetzt. Diese drei Haken rechnet das ' +
      'System aus — anklicken kannst du sie nicht.',
    seite: 'top'
  },
  {
    // Der Zettel gehört an die Stelle, an der er im Laden entsteht: die
    // Bücher liegen auf dem Stapel und brauchen ihre Beschriftung. In
    // Kapitel 4 stand er als nachgereichte Information und kam damit
    // Stunden zu spät.
    anker: 'detail-drucken',
    kontext: 'detail',
    titel: 'Der Zettel',
    text: 'Die Bücher liegen im Stapel — druck den Zettel und leg ihn dazu.',
    seite: 'bottom'
  },
  {
    anker: 'detail-bereit',
    kontext: 'detail',
    titel: 'Bereit zur Abholung',
    // Der Knopf heißt je nach Versandart „abholbereit setzen" oder
    // „abschließen" (E19/A46). Die Übungsbestellung hat noch keine — dann
    // bleibt es beim neutralen „bereit setzen", und genau so steht es
    // hier. Wer den Wortlaut ändert, sieht bitte in lib/zustandLabel.ts
    // nach, welcher Fall in der Tour gilt.
    text:
      'Alles da. Klick auf „Alle eingetroffen — bereit setzen"; danach wählst du, ' +
      'wie die Ware zur Kundin kommt.',
    erwartung: { art: 'anker-da', anker: 'sendungsdialog' },
    seite: 'top'
  },
  {
    anker: 'sendungsdialog',
    kontext: 'sendungsdialog',
    titel: 'Abholung oder Versand?',
    text:
      'Wähl „Abholung" und bestätige. Damit weiß jede Kollegin, dass die Kundin ' +
      'selbst vorbeikommt.',
    erwartung: { art: 'anker-weg', anker: 'sendungsdialog' },
    seite: 'top'
  },
  {
    anker: 'detail-checkliste',
    kontext: 'detail',
    titel: 'Kundin benachrichtigen',
    text:
      'Du rufst an — das System schickt nichts von selbst. Setz den Haken bei ' +
      '„Kunde benachrichtigt".',
    erwartung: {
      art: 'gedrueckt',
      anker: 'detail-checkliste',
      beschriftung: 'Kunde benachrichtigt'
    },
    seite: 'top'
  },
  {
    anker: 'detail-kommunikation',
    kontext: 'detail',
    titel: 'Notieren, was besprochen wurde',
    text:
      'Schreib kurz auf, was am Telefon war — das liest die Kollegin morgen. ' +
      'Ändern kannst du den Eintrag noch 60 Minuten lang.',
    seite: 'top'
  },
  {
    anker: 'detail-sendungen',
    kontext: 'detail',
    titel: 'Abgeholt',
    text:
      'Die Kundin war da. Klick „Abgeholt markieren" und bestätige das ' +
      'Datum.',
    erwartung: {
      art: 'text-in-anker',
      anker: 'detail-sendungen',
      enthaelt: 'Abgeholt am'
    },
    seite: 'top'
  },
  {
    anker: 'detail-zustand',
    kontext: 'detail',
    titel: 'Abschließen',
    text:
      'Zum Schluss: Zustand auf „Abgeschlossen". Danach fragt das System vor ' +
      'jeder weiteren Änderung einmal nach.',
    erwartung: {
      art: 'text-in-anker',
      anker: 'detail-zustand',
      enthaelt: 'Abgeschlossen'
    },
    seite: 'right'
  }
];

// ---------------------------------------------------------------------
// Kapitel 4 — Gut zu wissen
// ---------------------------------------------------------------------

const WISSEN: TourSchritt[] = [
  {
    // Kein Seitenwechsel: der Satz schliesst an den Zettel aus Kapitel 3
    // an, und wer gerade in der Bestellung steht, soll dort bleiben. Alle
    // Anker dieses Kapitels sind global und darum ueberall zu finden.
    kontext: 'global',
    titel: 'Der QR-Code',
    text:
      'Mit angemeldetem Gerät führt der Code direkt in die Bestellung. ' +
      'Kundschaft landet damit auf glomer.com.'
  },
  {
    // Ein reiner Zeige-Schritt, ohne Erwartung: die Glocke füllt sich
    // durch die Arbeit der Kolleginnen, nicht durch eine Übung. Wer sie
    // einmal gesehen hat, sucht sie später an der richtigen Stelle.
    anker: 'glocke',
    kontext: 'global',
    titel: 'Wenn dich jemand braucht',
    text:
      'Schreibt dich jemand mit @ in eine Notiz oder ein Gespräch, findest ' +
      'du es hier — mit einem Klick direkt in der Bestellung.',
    seite: 'bottom'
  },
  {
    anker: 'benutzermenue',
    kontext: 'global',
    // Am Telefon liegt der Nutzerwechsel im Menü — erst aufziehen, sonst
    // zeigt der Schritt auf etwas, das noch niemand sieht.
    vorher: 'menue-oeffnen',
    titel: 'Wer gerade arbeitet',
    // Ohne „oben rechts": am Telefon steht es unten im Menü. Ein Satz,
    // der den Ort festschreibt, wird in einem der beiden Layouts falsch.
    text:
      'Hier steht, in wessen Namen gespeichert wird. Am gemeinsamen Gerät ' +
      'vor dem Arbeiten kurz prüfen.',
    seite: 'bottom'
  },
  {
    kontext: 'global',
    titel: 'Ein Satz zum Merken',
    text:
      'Änderungen in Glomer kommen nicht automatisch hierher. Nur Stornos ' +
      'erfahren wir — alles andere musst du hier nachziehen.'
  },
  {
    anker: 'hilfe',
    kontext: 'global',
    vorher: 'menue-oeffnen',
    titel: 'Wo du das wiederfindest',
    text:
      'Hier kannst du jedes Kapitel jederzeit wiederholen — auch einzeln, ' +
      'wenn du nur eine Sache nachschlagen willst.',
    seite: 'right'
  },
  {
    kontext: 'global',
    vorher: 'aufraeumen',
    titel: 'Fertig',
    text:
      'Das war es — die Übungsbestellung ist wieder weg. Jedes Kapitel steht ' +
      'unter „Hilfe" und lässt sich einzeln wiederholen.'
  }
];

export const KAPITEL: readonly TourKapitel[] = [
  {
    id: 'orientierung',
    titel: 'Orientierung',
    kurz: 'Was auf dem Dashboard steht und wie du dich zurechtfindest.',
    brauchtUebung: false,
    schritte: ORIENTIERUNG
  },
  {
    id: 'erfassung',
    titel: 'Erfassung üben',
    kurz: 'Eine Bestellung aufnehmen, Schritt für Schritt.',
    brauchtUebung: false,
    schritte: ERFASSUNG
  },
  {
    id: 'lebenszyklus',
    titel: 'Lebenszyklus üben',
    kurz: 'Von „bestellt" bis „abgeschlossen" — an einer Übungsbestellung.',
    brauchtUebung: true,
    schritte: LEBENSZYKLUS
  },
  {
    id: 'wissen',
    titel: 'Gut zu wissen',
    kurz: 'QR-Code, Nutzerwechsel, und was Shopify nicht tut.',
    // Seit der Zettel in Kapitel 3 steht, haengt hier kein Schritt mehr
    // an einer Bestellung — alle sind global. Einzeln gestartet legt das
    // Kapitel deshalb nichts mehr an.
    brauchtUebung: false,
    schritte: WISSEN
  }
] as const;

/**
 * Schritte eines Kapitels, jeder mit seiner Kapitelüberschrift versehen.
 * Die Überschrift steht dann im Fortschritt der Sprechblase.
 */
export function schritteVon(k: TourKapitel): TourSchritt[] {
  return k.schritte.map((s) => ({ ...s, kapitelTitel: k.titel }));
}

/** Alle Schritte hintereinander — die vollständige Tour. */
export function alleSchritte(): TourSchritt[] {
  return KAPITEL.flatMap(schritteVon);
}

/** Jeder Anker mit dem Kontext, in dem er vorkommt. Für den Wächter-Test. */
export function ankerMitKontext(): { anker: string; kontext: TourKontext }[] {
  const gesehen = new Set<string>();
  const out: { anker: string; kontext: TourKontext }[] = [];
  for (const s of alleSchritte()) {
    // Der hervorgehobene Anker und die mitgenannten zählen gleich viel:
    // ein Schritt, der von der Kamera spricht, hängt genauso an ihr wie
    // am Suchfeld.
    for (const a of [s.anker, ...(s.mitAnker ?? [])]) {
      if (a === undefined) continue;
      const schluessel = `${s.kontext}:${a}`;
      if (gesehen.has(schluessel)) continue;
      gesehen.add(schluessel);
      out.push({ anker: a, kontext: s.kontext });
    }
  }
  // Anker, die nur in Erwartungen vorkommen, zählen genauso: bricht einer
  // weg, bleibt die Tour an dieser Stelle hängen.
  for (const s of alleSchritte()) {
    const e = s.erwartung;
    if (e === undefined) continue;
    if (e.art === 'route-enthaelt') continue;
    const kontext: TourKontext = e.anker === 'sheet' ? 'sheet' : e.anker === 'sendungsdialog' ? 'sendungsdialog' : s.kontext;
    const schluessel = `${kontext}:${e.anker}`;
    if (gesehen.has(schluessel)) continue;
    gesehen.add(schluessel);
    out.push({ anker: e.anker, kontext });
  }
  return out;
}
